import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { EXPERIMENTS } from "@/data/experiments";
import { Section, SectionHeading, StatusTag } from "@/components/shared/Primitives";
import { revealStaggered, VIEW_NEAR } from "@/components/shared/motion";

export default function ExperimentsPreview() {
  return (
    <Section id="experiments" testId="experiments-section">
      <div className="flex flex-wrap items-end justify-between gap-8">
        <SectionHeading
          overline="Experiments"
          title="An active creative technology laboratory."
          lead="Ongoing R&D that exists to answer one question at a time — not client work, and not decoration."
        />
        <Link
          to="/experiments"
          data-testid="experiments-all-link"
          className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#06B6D4] transition-colors duration-300 hover:text-[#F4F6FB]"
        >
          All experiments →
        </Link>
      </div>
      <div className="mt-16 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
        {EXPERIMENTS.map((e, i) => (
          <motion.div
            key={e.id}
            variants={revealStaggered}
            initial="hidden"
            whileInView="show"
            viewport={VIEW_NEAR}
            custom={i * 0.08}
          >
            <Link
              to={`/experiments/${e.slug}`}
              data-testid={`experiment-card-${e.id}`}
              className="group block overflow-hidden rounded-2xl border border-white/10 bg-[#12141C]/45 backdrop-blur-xl transition-colors duration-500 hover:border-[#8B5CF6]/40 focus-visible:outline-none focus-visible:border-[#06B6D4]"
            >
              <div className="relative overflow-hidden">
                <img
                  src={e.image}
                  alt={e.alt}
                  loading="lazy"
                  decoding="async"
                  className="h-52 w-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.06]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#12141C] via-transparent to-transparent" />
                <div className="absolute left-4 top-4">
                  <StatusTag tone="concept">Experiment / R&D</StatusTag>
                </div>
              </div>
              <div className="p-7">
                <div className="flex items-start justify-between gap-4">
                  <h3 className="font-display text-base font-semibold uppercase tracking-[0.12em] text-[#F4F6FB] md:text-lg">
                    {e.title}
                  </h3>
                  <ArrowUpRight className="h-4 w-4 shrink-0 text-[#8B5CF6] transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                </div>
                <p className="mt-3 text-sm leading-relaxed text-[#F4F6FB]/55">{e.summary}</p>
                <p className="mt-5 font-mono text-[10px] uppercase tracking-[0.25em] text-[#F4F6FB]/35">
                  {e.tech.slice(0, 3).join(" · ")}
                </p>
              </div>
            </Link>
          </motion.div>
        ))}
      </div>
    </Section>
  );
}
